import React, { FC, useRef, useState } from "react";
import { DATE_FORMAT, ICON_NAMES } from "utils/constant";
import { useTranslation } from "react-i18next";
import { SalesNoteJournalFilter } from "api/sales";
import DatePickerComponent from "ui/Date";
import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import {
	getBuyersFilterApi,
	getCatchAreaApi,
	getConditionApi,
	getFishingGearApi,
	getPreservationsApi,
	getSpecies,
	getTerminalsApi,
} from "api/common";
import { FishingGear, DropDownItem } from "interface/common";
import { Buyer } from "interface/buyer";
import { Vessel } from "interface/vessel";
import { getVesselFilter } from "api/vessel";
import { VesselRenderItem } from "ui/dropdownItemList/VesselRenderItem";
import { startOfDay } from "date-fns";

interface CustomProps {
	pageSize: number;
	setFilterOptions: (options: SalesNoteJournalFilter) => void;
	filterOptions: SalesNoteJournalFilter;
	exportAsPDF: () => void;
	exportAsExcel: () => void;
}

const Filter: FC<CustomProps> = ({
	setFilterOptions,
	filterOptions,
	exportAsPDF,
	...props
}) => {
	const { t } = useTranslation();

	const [localFromDate, setLocalFromDate] = useState<Date | undefined>(
		filterOptions.dateFrom,
	);
	const [localToDate, setLocalToDate] = useState<Date | undefined>(
		filterOptions.dateTo,
	);
	const pendingFromDate = useRef<Date | undefined>(filterOptions.dateFrom);
	const pendingToDate = useRef<Date | undefined>(filterOptions.dateTo);

	const isSameDate = (
		a: Date | null | undefined,
		b: Date | null | undefined,
	) => {
		if (!a || !b) return a === b;
		return startOfDay(a).getTime() === startOfDay(b).getTime();
	};

	const applyFromDate = (value = pendingFromDate.current) => {
		if (isSameDate(value, filterOptions.dateFrom)) return;
		setFilterOptions({
			...filterOptions,
			dateFrom: value,
			take: props.pageSize,
			skip: 0,
		});
	};

	const applyToDate = (value = pendingToDate.current) => {
		if (isSameDate(value, filterOptions.dateTo)) return;
		setFilterOptions({
			...filterOptions,
			dateTo: value,
			take: props.pageSize,
			skip: 0,
		});
	};

	const handleVessel = (vessel: Vessel) => {
		setFilterOptions({
			...filterOptions,
			fishermanLedgerAccount: vessel.res as any,
			vesselName: vessel.id ? vessel.nm : "",
			take: props.pageSize,
			skip: 0,
		});
	};

	return (
		<div className="filter-container">
			<div className="filter-items frozen-sales-filter-items">
				<div className="frozen-sales-filter-field">
					<DatePickerComponent
						placeholder={t("FromDate")}
						label={t("FromDate")}
						className="height-inherit"
						format={DATE_FORMAT}
						value={localFromDate}
						onChange={(e) => {
							const value = e.value ?? undefined;
							pendingFromDate.current = value;
							setLocalFromDate(value);
							if (e.syntheticEvent?.type === "click") {
								applyFromDate(value);
							}
						}}
						onBlur={() => applyFromDate()}
					/>
				</div>

				<div className="frozen-sales-filter-field">
					<DatePickerComponent
						placeholder={t("ToDate")}
						label={t("ToDate")}
						className="height-inherit"
						format={DATE_FORMAT}
						value={localToDate}
						onChange={(e) => {
							const value = e.value ?? undefined;
							pendingToDate.current = value;
							setLocalToDate(value);
							if (e.syntheticEvent?.type === "click") {
								applyToDate(value);
							}
						}}
						onBlur={() => applyToDate()}
					/>
				</div>

				<div className="frozen-sales-filter-field">
					<CustomDropDownList
						data={[]}
						filterable={true}
						dataItemKey="id"
						textField="nm"
						label={t("Vessel")}
						value={{
							id: filterOptions.fishermanLedgerAccount || null,
							nm: filterOptions.vesselName || " ",
						}}
						defaultItem={{ id: null, nm: t("All") }}
						onSelect={handleVessel}
						serverFilter={true}
						itemRender={VesselRenderItem()}
						serverFilterApi={getVesselFilter}
						className="height-inherit"
					/>
				</div>

				<div className="frozen-sales-filter-field">
					<CustomDropDownList
						data={[]}
						label={t("Buyer")}
						dataItemKey="id"
						textField="displayName"
						value={{
							id: filterOptions.byerLedgerAccount || null,
							displayName: filterOptions.fishermanName || " ",
						}}
						defaultItem={{ id: null, displayName: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getBuyersFilterApi}
						onSelect={(buyer: Buyer) =>
							setFilterOptions({
								...filterOptions,
								byerLedgerAccount: buyer.id as any,
								fishermanName: buyer.name,
								take: props.pageSize,
								skip: 0,
							})
						}
						className="height-inherit"
						popupSettings={{ width: 400 }}
					/>
				</div>

				<div className="frozen-sales-filter-field">
					<CustomDropDownList
						data={[]}
						label={t("CatchArea")}
						dataItemKey="zone"
						textField="displayName"
						value={{
							zone: filterOptions.catchField1 || null,
							displayName: filterOptions.catchField1DisplayName || " ",
						}}
						defaultItem={{ zone: null, displayName: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getCatchAreaApi}
						onSelect={(item) => {
							setFilterOptions({
								...filterOptions,
								catchField1: item.code ?? undefined,
								catchField1DisplayName: item.displayName,
								take: props.pageSize,
								skip: 0,
							});
						}}
						className="height-inherit"
						popupSettings={{ width: 400 }}
					/>
				</div>

				{/*<div style={{ width: "170px" }}>*/}
				{/*    <CustomDropDownList*/}
				{/*        data={[]}*/}
				{/*        label={t("Terminal")}*/}
				{/*        dataItemKey="code"*/}
				{/*        textField="displayName"*/}
				{/*        value={{*/}
				{/*            code: filterOptions.terminal || null,*/}
				{/*            displayName: filterOptions.terminalName || " ",*/}
				{/*        }}*/}
				{/*        defaultItem={{ code: null, displayName: t("All") }}*/}
				{/*        filterable={true}*/}
				{/*        serverFilter={true}*/}
				{/*        serverFilterApi={getTerminalsApi}*/}
				{/*        onSelect={(item: DropDownItem) =>*/}
				{/*            setFilterOptions({*/}
				{/*                ...filterOptions,*/}
				{/*                terminal: item.code ?? undefined,*/}
				{/*                terminalName: item.displayName,*/}
				{/*                take: props.pageSize,*/}
				{/*                skip: 0,*/}
				{/*            })*/}
				{/*        }*/}
				{/*        className="height-inherit"*/}
				{/*    />*/}
				{/*</div>*/}

				<div className="frozen-sales-filter-field">
					<CustomDropDownList
						data={[]}
						label={t("Species")}
						dataItemKey="code"
						textField="name"
						value={{
							code: filterOptions.species || null,
							name: filterOptions.speciesName || " ",
						}}
						defaultItem={{ code: null, name: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getSpecies}
						onSelect={(item) =>
							setFilterOptions({
								...filterOptions,
								species: item.code ?? undefined,
								speciesName: item.name,
								take: props.pageSize,
								skip: 0,
							})
						}
						className="height-inherit"
						popupSettings={{ width: 400 }}
					/>
				</div>

				<div className="frozen-sales-filter-field">
					<CustomDropDownList
						data={[]}
						label={t("FishingGear")}
						dataItemKey="code"
						textField="displayName"
						value={{
							code: filterOptions.fishingGearCode || null,
							displayName: filterOptions.fishingGearName || " ",
						}}
						defaultItem={{ code: null, displayName: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getFishingGearApi}
						onSelect={(gear: FishingGear) =>
							setFilterOptions({
								...filterOptions,
								fishingGearCode: gear.code ?? undefined,
								fishingGearName: gear.name,
								take: props.pageSize,
								skip: 0,
							})
						}
						className="height-inherit"
						popupSettings={{ width: 400 }}
					/>
				</div>

				<div className="frozen-sales-filter-field">
					<CustomDropDownList
						data={[]}
						label={t("Condition")}
						dataItemKey="code"
						textField="displayName"
						value={{
							code: filterOptions.conditionCode || null,
							displayName: filterOptions.conditionName || " ",
						}}
						defaultItem={{ code: null, displayName: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getConditionApi}
						onSelect={(item: DropDownItem) =>
							setFilterOptions({
								...filterOptions,
								conditionCode: item.code ?? undefined,
								conditionName: item.displayName,
								take: props.pageSize,
								skip: 0,
							})
						}
						className="height-inherit"
					/>
				</div>

				<div className="frozen-sales-filter-field">
					<CustomDropDownList
						data={[]}
						label={t("PreservationType")}
						dataItemKey="code"
						textField="displayName"
						value={{
							code: filterOptions.preservationCode || null,
							displayName: filterOptions.preservationName || " ",
						}}
						defaultItem={{ code: null, displayName: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getPreservationsApi}
						onSelect={(item: DropDownItem) =>
							setFilterOptions({
								...filterOptions,
								preservationCode: item.code ?? undefined,
								preservationName: item.displayName,
								take: props.pageSize,
								skip: 0,
							})
						}
						className="height-inherit"
						popupSettings={{ width: 300 }}
					/>
				</div>
			</div>

			<div className="filter-end-btn frozen-sales-filter-end-btn">
				<CustomButton
					label={"PDF"}
					onClick={() => exportAsPDF()}
					iconName={ICON_NAMES.Download}
				/>
				<CustomButton
					label={"Excel"}
					onClick={() => props.exportAsExcel()}
					iconName={ICON_NAMES.Download}
				/>
			</div>
		</div>
	);
};

export default Filter;
